import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    try {
      console.log('AuthContext: Fetching user...');
      const userData = await authAPI.getCurrentUser();
      console.log('AuthContext: User data received:', userData);
      setUser(userData);
    } catch (error) {
      console.error('AuthContext: Failed to fetch user:', error);
      // Don't call logout here, just set loading to false
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    try {
      console.log('AuthContext: Attempting login...');
      const response = await authAPI.login(email, password);
      console.log('AuthContext: Login response:', response);
      const { access_token } = response;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      await fetchUser();
      return response;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      throw error;
    }
  };

  const register = async (email, username, password) => {
    try {
      const response = await authAPI.register(email, username, password);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const acceptSeatInvite = async (inviteToken, password) => {
    const response = await authAPI.acceptSeatInvite(inviteToken, password);
    const { access_token } = response;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    await fetchUser();
    return response;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    acceptSeatInvite,
    isAuthenticated: !!token && !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
